import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTH_ERRORS = {
  auth_denied: 'You denied access. Please try again.',
  auth_failed: 'Authentication failed. Please try again.',
  invalid_callback: 'Invalid authentication callback.',
  no_email: 'Could not get your email from Google.',
  not_registered: 'Your email is not registered. Contact your administrator.',
  account_inactive: 'Your account has been deactivated. Contact admin.',
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const authError = searchParams.get('error');

  useEffect(() => {
    loadSettings();
  }, []);

  // Auto-advance slider
  useEffect(() => {
    if (!settings?.slider_enabled || !settings?.slider_cards?.length) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % settings.slider_cards.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [settings]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
      setSettings({
        app_name: 'Rajje Pulse',
        login_title: 'Welcome Back',
        login_subtitle: 'Sign in to access your campaign dashboard',
        login_tagline: 'From voices to verified history',
        login_logo_url: '',
        slider_enabled: false,
        slider_cards: []
      });
    } finally {
      setSettingsLoaded(true);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth-callback';
    const authUrl = `${BACKEND_URL}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const nextSlide = () => {
    if (!settings?.slider_cards?.length) return;
    setCurrentSlide((prev) => (prev + 1) % settings.slider_cards.length);
  };

  const prevSlide = () => {
    if (!settings?.slider_cards?.length) return;
    setCurrentSlide((prev) => (prev - 1 + settings.slider_cards.length) % settings.slider_cards.length);
  };

  // Show loading spinner until settings are loaded
  if (!settingsLoaded || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1628]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  const hasSlider = settings.slider_enabled && settings.slider_cards && settings.slider_cards.length > 0;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Dark Branding Panel */}
      <div className="lg:w-1/2 bg-[#0a1628] relative overflow-hidden flex flex-col items-center justify-center p-8 lg:p-12 min-h-[40vh] lg:min-h-screen">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Particle wave effect at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-64">
            <svg className="w-full h-full" viewBox="0 0 1200 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0891b2" stopOpacity="0.1" />
                  <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#0891b2" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              <path 
                d="M0,100 Q300,150 600,100 T1200,100 L1200,200 L0,200 Z" 
                fill="url(#waveGradient)"
                className="animate-pulse"
              />
            </svg>
          </div>
          
          {/* Floating particles */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-cyan-400/20 animate-float"
                style={{
                  width: Math.random() * 6 + 2 + 'px',
                  height: Math.random() * 6 + 2 + 'px',
                  left: Math.random() * 100 + '%',
                  top: Math.random() * 100 + '%',
                  animationDelay: Math.random() * 5 + 's',
                  animationDuration: 5 + Math.random() * 10 + 's'
                }}
              />
            ))}
          </div>
          
          {/* Glow effect behind logo */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        
        {/* Logo and Branding */}
        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Logo - from settings or placeholder space */}
          <div className="mb-8">
            {settings.login_logo_url ? (
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-xl animate-pulse" />
                <img 
                  src={settings.login_logo_url} 
                  alt="Logo" 
                  className="relative w-32 h-32 lg:w-48 lg:h-48 object-contain drop-shadow-[0_0_30px_rgba(34,211,238,0.5)]"
                />
              </div>
            ) : (
              <div className="h-32 lg:h-48" /> // Empty space when no logo
            )}
          </div>
          
          {/* App Name */}
          <h1 className="text-3xl lg:text-5xl font-bold mb-3">
            <span className="text-white">Rajje</span>
            <span className="text-cyan-400 ml-2">Pulse</span>
          </h1>
          
          {/* Tagline */}
          <p className="text-gray-400 text-base lg:text-lg">
            From voices to <span className="text-cyan-400">verified</span> history
          </p>

          {/* Slider Section */}
          {hasSlider && (
            <div className="mt-8 w-full max-w-md">
              <div className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                {/* Slide Content */}
                <div className="min-h-[80px]">
                  <h3 className="text-cyan-400 font-semibold text-lg mb-2">
                    {settings.slider_cards[currentSlide]?.title}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {settings.slider_cards[currentSlide]?.description}
                  </p>
                </div>
                
                {/* Navigation */}
                {settings.slider_cards.length > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={prevSlide}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Dots */}
                    <div className="flex gap-2">
                      {settings.slider_cards.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentSlide(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === currentSlide ? 'bg-cyan-400 w-4' : 'bg-gray-500'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={nextSlide}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Bottom gradient line (mobile only visible) */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent lg:hidden" />
      </div>

      {/* Right Side - Login Form */}
      <div className="lg:w-1/2 bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-6 lg:p-12 flex-1">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
            <CardContent className="p-8 lg:p-10">
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                  {settings.login_title || 'Welcome Back'}
                </h2>
                <p className="text-gray-500">
                  {settings.login_subtitle || 'Sign in to access your campaign dashboard'}
                </p>
              </div>

              {authError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  {AUTH_ERRORS[authError] || 'Authentication failed. Please try again.'}
                </div>
              )}
              {/* Google Sign In Button */}
              <Button
                onClick={handleGoogleLogin}
                className="w-full h-14 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl font-medium text-base transition-all duration-200 shadow-lg hover:shadow-xl"
                data-testid="google-login-button"
              >
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              {/* Help Text */}
              <p className="mt-6 text-center text-sm text-gray-500">
                Contact your administrator if you don't have access
              </p>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              © 2025 <span className="font-medium text-gray-700">Rajje Pulse</span>. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx="true">{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
        }
        .animate-float {
          animation: float ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
